(() => {
  if (customElements.get('obd-review-wall')) return;
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = String(text).replace(/</g, '&lt;').replace(/>/g, '&gt;');
      node.textContent = decoder.value;
    }
    return node;
  };
  const safeURL = (value, local = false) => {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || (local && url.origin !== location.origin)) return null;
      return url.href;
    } catch { return null; }
  };
  class ReviewWall extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.filter = this.querySelector('[data-review-filter]');
      this.list = this.querySelector('[data-review-list]');
      this.status = this.querySelector('[data-review-status]');
      this.more = this.querySelector('[data-review-more]');
      this.error = this.querySelector('[data-review-error]');
      this.seen = new Set();
      const initial = new URL(location.href).searchParams.get('review_product');
      if (initial && [...this.filter.options].some(option => option.value === initial)) this.filter.value = initial;
      this.filter.addEventListener('change', () => {
        const url = new URL(location.href);
        if (this.filter.value === 'all') url.searchParams.delete('review_product');
        else url.searchParams.set('review_product', this.filter.value);
        history.replaceState(null, '', url);
        this.load(1, true);
      });
      this.more.addEventListener('click', () => this.load(this.page + 1));
      this.querySelector('[data-review-retry]').addEventListener('click', () => this.load(this.failedPage || 1, (this.failedPage || 1) === 1));
      this.load(1, true);
    }
    disconnectedCallback() { this.controller?.abort(); }
    async load(page, reset = false) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const selected = this.filter.value;
      const all = selected === 'all' || selected === 'store';
      const url = new URL(all ? 'https://api.judge.me/reviews/all_reviews_js_based' : 'https://api.judge.me/reviews/reviews_for_widget');
      const params = { shop_domain: this.dataset.shop, platform: 'shopify', page, sort_by: 'created_at', sort_dir: 'desc' };
      if (all) Object.assign(params, { widget_type: 'all-reviews-widget-v2025', review_type: selected === 'store' ? 'shop-reviews' : 'all-reviews', require_json: 'true' });
      else Object.assign(params, { product_id: selected, per_page: 12 });
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, String(value)));
      this.error.hidden = true;
      this.more.disabled = true;
      this.more.textContent = 'Loading…';
      this.list.setAttribute('aria-busy', 'true');
      if (reset) {
        this.list.replaceChildren();
        this.seen.clear();
        this.page = 0;
        this.more.hidden = true;
        this.status.textContent = 'Loading reviews…';
      }
      const timeout = setTimeout(() => controller.abort('timeout'), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review feed unavailable');
        const data = await response.json();
        if (!Array.isArray(data.reviews) || !data.pagination) throw new Error('Unexpected review data');
        if (this.controller !== controller) return;
        const fragment = document.createDocumentFragment();
        for (const review of data.reviews) {
          if (!review.uuid || this.seen.has(review.uuid)) continue;
          this.seen.add(review.uuid);
          fragment.append(this.card(review));
        }
        this.list.append(fragment);
        this.page = Number(data.pagination.current_page) || page;
        const total = Number(data.pagination.total_count ?? data.number_of_reviews ?? (selected === 'store' ? data.number_of_shop_reviews : NaN));
        this.more.hidden = this.page >= Number(data.pagination.total_pages || 0);
        const label = this.filter.selectedOptions[0].textContent;
        this.status.textContent = this.seen.size
          ? `Showing ${this.seen.size}${Number.isFinite(total) ? ` of ${total}` : ''} reviews · Newest first`
          : selected === 'all' ? 'No reviews yet.' : `No reviews yet for ${label}.`;
        this.failedPage = null;
      } catch (error) {
        if (this.controller !== controller) return;
        this.failedPage = page;
        this.error.hidden = false;
        this.status.textContent = this.seen.size ? `Showing ${this.seen.size} reviews. More reviews couldn't load.` : 'Reviews are temporarily unavailable.';
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.list.setAttribute('aria-busy', 'false');
          this.more.disabled = false;
          this.more.textContent = 'Load more reviews';
        }
      }
    }
    card(review) {
      const card = make('article', 'obd-wall__card');
      card.dataset.reviewId = review.uuid;
      const top = make('div', 'obd-wall__card-top');
      const rating = Math.max(1, Math.min(5, Number(review.rating) || 1));
      const stars = make('span', 'obd-wall__stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      for (let index = 1; index <= 5; index++) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        if (index > rating) svg.classList.add('empty');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'm12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01Z');
        svg.append(path); stars.append(svg);
      }
      top.append(stars);
      if (review.verified_buyer) top.append(make('span', 'obd-wall__verified', 'Verified buyer'));
      card.append(top);
      if (review.title) card.append(make('h2', '', review.title));
      if (review.body) card.append(make('p', 'obd-wall__body', review.body));
      const photos = make('div', 'obd-wall__photos');
      for (const [index, photo] of (review.pictures_urls || []).entries()) {
        const src = safeURL(typeof photo === 'string' ? photo : photo.small || photo.compact || photo.original || photo.huge);
        if (!src) continue;
        const link = make('a'); link.href = safeURL(photo.original || photo.huge) || src;
        link.target = '_blank'; link.rel = 'noopener';
        const image = make('img'); image.src = src; image.alt = `Customer review photo ${index + 1}`; image.loading = 'lazy'; image.width = 72; image.height = 72;
        link.append(image); photos.append(link);
      }
      if (photos.children.length) card.append(photos);
      if (review.reply_content) {
        const reply = make('div', 'obd-wall__reply');
        reply.append(make('strong', '', 'OBD Collectibles replied'), make('p', '', review.reply_content)); card.append(reply);
      }
      const byline = make('p', 'obd-wall__byline');
      byline.append(make('strong', '', review.is_anonymous_reviewer ? 'Anonymous' : review.reviewer_name || 'Customer'));
      const date = new Date(review.created_at);
      if (!Number.isNaN(date.getTime())) {
        const time = make('time', '', new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date));
        time.dateTime = date.toISOString(); byline.append(time);
      }
      card.append(byline);
      const productURL = review.is_shop_review ? null : safeURL(review.product_url, true);
      const product = make(productURL ? 'a' : 'span', 'obd-wall__product', review.is_shop_review ? 'Store review' : review.product_title || 'Product review');
      if (productURL) product.href = productURL;
      card.append(product);
      return card;
    }
  }
  customElements.define('obd-review-wall', ReviewWall);
})();
